import { useCallback, useState } from "react";
import { requestPayment } from "../services/payments";
import { useUserState } from "../state/user";
import useProfessionalAccountStatus from "./useProfessionalAccountStatus";
import {
  FINAL_CONSUMER_FISCAL_NUMBER,
  PaymentConfiguration,
  RequestPaymentInput,
  TransactionLine
} from "../types/payments";
import { round2 } from "../utils/money";

/** A payment line as held in the form, before it is sent to the API. */
export interface PaymentLine {
  id: string;
  code: string;
  description: string;
  unitCost: number;
  quantity: number;
}

export interface RequestPaymentParams {
  config: PaymentConfiguration;
  patientId: string;
  patientName: string;
  email: string;
  /** The patient's tax number, or undefined for a final consumer. */
  fiscalNumber?: string;
  lines: PaymentLine[];
}

export const linesTotal = (lines: PaymentLine[]) =>
  lines.reduce((total, line) => total + line.unitCost * line.quantity, 0);

export default function useRequestPayment() {
  const { token } = useUserState();
  const { paymentId, skuId } = useProfessionalAccountStatus();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const request = useCallback(
    async ({
      config,
      patientId,
      patientName,
      email,
      fiscalNumber,
      lines
    }: RequestPaymentParams) => {
      if (!token || !paymentId || !skuId) {
        throw new Error("Missing professional account details");
      }

      const items: TransactionLine[] = lines.map(line => ({
        code: line.code,
        description: line.description,
        // The API expects the unit price here, not the line total.
        value: round2(line.unitCost),
        quantity: line.quantity
      }));

      const input: RequestPaymentInput = {
        configId: config.id,
        issuer: paymentId,
        receiver: patientId,
        taxFree: true,
        planId: skuId,
        customer: {
          name: patientName,
          email,
          fiscal_number: fiscalNumber || FINAL_CONSUMER_FISCAL_NUMBER,
          // The mobile app always sends these empty, so the API expects them.
          phone_indicative: "",
          phone: ""
        },
        order: { value: round2(linesTotal(lines)), items }
      };

      try {
        setLoading(true);
        setError(null);
        return await requestPayment({ authToken: token, input });
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [token, paymentId, skuId]
  );

  return { request, loading, error };
}
